<script setup lang="ts">
import type { FieldSize } from '@/components/fields/KTextField.vue';

import { computed, ref, useTemplateRef, watch } from 'vue';
import { VueDatePicker } from '@vuepic/vue-datepicker';
import { format } from 'date-fns-tz';

import { checkValidDate } from '@/utils/date';
import FallbackMdiIconCmn from '@/components/cmns/FallbackMdiIconCmn.vue';
import KTextField from '@/components/fields/KTextField.vue';
import usePickerField from '@/composables/usePickerField';
import { useUserStore } from '@/stores/useUserStore';
import * as dateHelpers from '@/helpers/date';
import * as datePickerLabels from '@/constants/datePickerLabels';

import datepickerStyles from '@/styles/datepicker.module.scss';

export type DateRangePickerFieldValue = [Date, Date];

export interface Props {
  modelValue?: DateRangePickerFieldValue | null;
  label?: string;
  loading?: boolean;
  disabled?: boolean;
  hideDetails?: boolean;
  rules?: object;
  size?: FieldSize;
  autoApply?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  label: 'Range',
  loading: false,
  disabled: false,
  hideDetails: false,
  rules: () => {
    return [];
  },
  size: undefined,
  autoApply: false,
});
const emit = defineEmits<{
  'update:modelValue': [value: DateRangePickerFieldValue | null];
}>();

const fieldFormatPartPattern = 'yyyy-MM-dd';

const fieldValue = ref<DateRangePickerFieldValue | null>();
const datepickerEl = useTemplateRef('datepicker');
const root = ref<HTMLElement | null>(null);

const userStore = useUserStore();
const userTimeZone = computed(() => userStore.user.timezone);

const presetDates = ref([
  {
    label: datePickerLabels.TODAY,
    value: dateHelpers.getDatePickerPresetValue(
      datePickerLabels.TODAY,
      userTimeZone.value,
    ),
  },
  {
    label: datePickerLabels.YESTERDAY,
    value: dateHelpers.getDatePickerPresetValue(
      datePickerLabels.YESTERDAY,
      userTimeZone.value,
    ),
  },
  {
    label: datePickerLabels.TOMORROW,
    value: dateHelpers.getDatePickerPresetValue(
      datePickerLabels.TOMORROW,
      userTimeZone.value,
    ),
  },
  {
    label: datePickerLabels.LAST_7_DAYS,
    value: dateHelpers.getDatePickerPresetValue(
      datePickerLabels.LAST_7_DAYS,
      userTimeZone.value,
    ),
  },
  {
    label: datePickerLabels.LAST_14_DAYS,
    value: dateHelpers.getDatePickerPresetValue(
      datePickerLabels.LAST_14_DAYS,
      userTimeZone.value,
    ),
  },
  {
    label: datePickerLabels.NEXT_5_DAYS,
    value: dateHelpers.getDatePickerPresetValue(
      datePickerLabels.NEXT_5_DAYS,
      userTimeZone.value,
    ),
  },
  {
    label: datePickerLabels.THIS_WEEK,
    value: dateHelpers.getDatePickerPresetValue(
      datePickerLabels.THIS_WEEK,
      userTimeZone.value,
    ),
  },
  {
    label: datePickerLabels.THIS_MONTH,
    value: dateHelpers.getDatePickerPresetValue(
      datePickerLabels.THIS_MONTH,
      userTimeZone.value,
    ),
  },
  {
    label: datePickerLabels.LAST_MONTH,
    value: dateHelpers.getDatePickerPresetValue(
      datePickerLabels.LAST_MONTH,
      userTimeZone.value,
    ),
  },
  {
    label: datePickerLabels.THIS_YEAR,
    value: dateHelpers.getDatePickerPresetValue(
      datePickerLabels.THIS_YEAR,
      userTimeZone.value,
    ),
  },
  {
    label: datePickerLabels.LAST_6_MTHS_NEXT_6_MTHS,
    value: dateHelpers.getDatePickerPresetValue(
      datePickerLabels.LAST_6_MTHS_NEXT_6_MTHS,
      userTimeZone.value,
    ),
  },
  {
    label: datePickerLabels.LAST_2_YEARS_NEXT_2_YEARS,
    value: dateHelpers.getDatePickerPresetValue(
      datePickerLabels.LAST_2_YEARS_NEXT_2_YEARS,
      userTimeZone.value,
    ),
  },
]);

function getFieldValue(value: unknown): DateRangePickerFieldValue | null {
  if (
    Array.isArray(value) &&
    checkValidDate(value[0]) &&
    checkValidDate(value[1])
  ) {
    return [value[0], value[1]];
  } else {
    return null;
  }
}

watch(
  () => props.modelValue,
  (newModelValue) => {
    if (newModelValue === undefined) {
      fieldValue.value = undefined;
    } else {
      fieldValue.value = getFieldValue(newModelValue);
    }
  },
  { immediate: true },
);

function handleUpdateModelValue(value: unknown): void {
  fieldValue.value = getFieldValue(value);
  emit('update:modelValue', fieldValue.value);
}

function formatDateRange(value: unknown): string {
  if (!Array.isArray(value)) return '';
  const fromDate = value[0];
  const toDate = value[1];
  let newValue = format(fromDate, fieldFormatPartPattern);
  if (checkValidDate(toDate)) {
    newValue += '–' + format(toDate, fieldFormatPartPattern);
  }
  return newValue;
}

const {
  pickerAttrs,
  handleAllEnterKeydown,
  handleAllEscKeydown,
  handleAppendIconClick,
  handleAllSpaceKeydown,
  handleDownKeydown,
} = usePickerField(root, datepickerEl);
</script>

<template>
  <div ref="root" data-testid="dateRangePickerField">
    <VueDatePicker
      v-bind="pickerAttrs"
      ref="datepicker"
      :model-value="fieldValue"
      :formats="{ input: formatDateRange }"
      :auto-apply="props.autoApply"
      :action-row="{ showNow: false }"
      :class="[
        $style.dateRangePickerField,
        {
          [$style.datepickerHideDetails]: props.hideDetails,
        },
      ]"
      :preset-dates="presetDates"
      :ui="{
        menu: datepickerStyles.dpCustomMenu,
        calendar: datepickerStyles.dpCustomCalendar,
      }"
      :time-config="{ enableTimePicker: false }"
      :text-input="{
        enterSubmit: true,
        tabSubmit: true,
        openMenu: !props.disabled ? 'open' : false,
      }"
      range
      @update:model-value="handleUpdateModelValue"
      @keydown.enter="handleAllEnterKeydown"
      @keydown.space="handleAllSpaceKeydown"
      @keydown.esc="handleAllEscKeydown"
    >
      <template #preset-date-range-button="{ value, presetDate }">
        <span
          role="button"
          :tabindex="0"
          @click="presetDate(value)"
          @keyup.enter.prevent="presetDate(value)"
          @keyup.space.prevent="presetDate(value)"
        >
          {{ label }}
        </span>
      </template>
      <template #arrow-left>
        <FallbackMdiIconCmn icon="mdi-chevron-left" />
      </template>
      <template #arrow-right>
        <FallbackMdiIconCmn icon="mdi-chevron-right" />
      </template>
      <template #arrow-up>
        <FallbackMdiIconCmn icon="mdi-chevron-up" />
      </template>
      <template #arrow-down>
        <FallbackMdiIconCmn icon="mdi-chevron-down" />
      </template>
      <template #calendar-icon>
        <FallbackMdiIconCmn icon="mdi-calendar-blank-outline" />
      </template>
      <template
        #dp-input="{ value, onInput, onTab, onClear, onBlur, isMenuOpen }"
      >
        <KTextField
          append-inner-icon="mdi-calendar-blank-outline"
          :label="props.label"
          clearable
          :model-value="value"
          :loading="props.loading"
          :disabled="props.disabled"
          :hide-details="props.hideDetails"
          :rules="props.rules"
          :size="props.size"
          :class="$style.field"
          readonly
          @input="onInput"
          @keydown.tab="onTab"
          @blur="onBlur"
          @keydown.down.prevent="handleDownKeydown($event, isMenuOpen)"
          @click:clear="onClear"
          @click:append-inner="handleAppendIconClick($event, isMenuOpen)"
        />
      </template>
    </VueDatePicker>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.dateRangePickerField {
  padding-bottom: 22px;
  :global {
    .dp--menu {
      margin-top: -10px;
    }
    .dp--input {
      height: 42px;
      box-shadow: rgba(0, 0, 0, 0.35) 0px 2px 2px;
      background-color: colors.$color-background-surface;
      @apply border-none rounded;
    }
    .dp--arrow-top {
      display: none;
    }
    .dp--input-icon {
      height: 100%;
    }
    .dp--main .dp--input-icon-pad {
      padding-top: 24px;
      padding-left: 16px;
    }
    .dp--preset-dates {
      padding-top: 6px;
      .dp--preset-range {
        padding: 6px;
        border: none;
        outline: none;
      }
      .dp--preset-range:not(:hover) {
        background: none;
      }
    }
  }
}

.datepickerHideDetails {
  padding-bottom: 0;
}

.field {
  :global {
    .v-field {
      cursor: pointer;
    }
    input.v-field__input {
      padding-right: 0;
      cursor: pointer;
    }
    .v-input__details {
      position: absolute;
      bottom: -22px;
    }
  }
}
</style>
